import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, Flex, PinInput, Text } from "@mantine/core";
import { Modal } from "../../../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import { ValidationPin } from "./schema-validation";
import {
  IPinValidation,
  pinValidation,
} from "../../../../hooks/useAuthService";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

interface IPropsPinCode {
  nextStep: () => void;
  type: string;
  alreadyPassword: boolean;
  returnStep: () => void;
  emailHash: string;
  userIp: string;
  setSecretPhrase: React.Dispatch<React.SetStateAction<string>>;
  setEmailHash: React.Dispatch<React.SetStateAction<string>>;
}

const PinCode: React.FC<IPropsPinCode> = ({
  nextStep,
  type,
  alreadyPassword,
  returnStep,
  emailHash,
  userIp,
  setSecretPhrase,
  setEmailHash,
}) => {
  const { t } = useTranslation();
  const [modalSuccess, setModalSuccess] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isLargerThan640 = useMediaQuery("(min-width: 640px)");

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
  } = useForm<IPinValidation>({
    mode: "all",
    resolver: yupResolver(ValidationPin),
    defaultValues: {
      user_email_hash: emailHash,
      secret_pin: "",
      client_ip: userIp,
    },
  });

  const onSubmit = async (data: IPinValidation) => {
    setIsLoading(true);
    try {
      const response = await pinValidation(data);
      setSecretPhrase(response.data.data.secret_phrase);
      setEmailHash(response.data.data.user_email_hash);
      setModalSuccess(!modalSuccess);
    } catch (error: any) {
      const erro = error.response.data;

      if (erro.statusCode === 404) {
        setErro(
          t("autenticacao.acessar_conta.forget_password.step_3.erro_404")
        );
      } else if (erro.statusCode === 403) {
        setErro(
          t("autenticacao.acessar_conta.forget_password.step_3.erro_403")
        );
      } else {
        setErro(t("autenticacao.acessar_conta.forget_password.step_3.erro"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        type="success"
        titleSeccondButton={t(
          "autenticacao.acessar_conta.forget_password.step_3.modal_success.title"
        )}
        description={t(
          "autenticacao.acessar_conta.forget_password.step_3.modal_success.description"
        )}
        open={modalSuccess}
        setClose={() => setModalSuccess(!modalSuccess)}
        secondClick={() => {
          nextStep();
          setModalSuccess(!modalSuccess);
        }}
      />
      {alreadyPassword ? (
        <Text fw={600} size={16} lh={"28px"} mb={6} ta="center">
          {t(
            "autenticacao.acessar_conta.forget_password.step_3.already_password"
          )}
        </Text>
      ) : null}
      <Text size={16} fw={400} mb={24} maw={602} ta="center">
        {t("autenticacao.acessar_conta.forget_password.step_3.text")}
      </Text>
      <Controller
        control={control}
        name="secret_pin"
        render={({ field: { onBlur, onChange } }) => (
          <PinInput
            onBlur={onBlur}
            onChange={onChange}
            length={6}
            type="alphanumeric"
            placeholder="*"
            styles={{
              input: {
                width: isLargerThan640 ? undefined : "36px",
                height: isLargerThan640 ? undefined : "36px",
                padding: isLargerThan640 ? undefined : "2px !important",
                fontSize: isLargerThan640 ? undefined : "20px !important",
              },
              root: {
                gap: isLargerThan640 ? undefined : "4px !important",
                justifyContent: isLargerThan640 ? undefined : "center",
              },
            }}
          />
        )}
      />

      <Flex w="100%" justify="space-between" mt={24}>
        <Text>
          {t(
            "autenticacao.acessar_conta.forget_password.step_3.not_receive_code"
          )}
        </Text>
        <Button h="auto" variant="link" onClick={returnStep}>
          {t("autenticacao.acessar_conta.forget_password.step_3.resend_code")}
        </Button>
      </Flex>

      <Button
        onClick={handleSubmit(onSubmit)}
        variant="primary"
        fullWidth
        mt={26}
        disabled={!isDirty || !isValid}
        loading={isLoading}
      >
        {t("autenticacao.acessar_conta.forget_password.step_3.button")}
      </Button>
      {erro && <Text color="red">{erro}</Text>}
    </>
  );
};

export default PinCode;
